import React, { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import { useChatContext } from "../../context/ChatContext";
import { authFetch, authPost } from "../../Utilities/Api";
import { usePublicProfileData } from "../../hooks/usePublicProfileData";
import ConfirmModal from "../../components/ConfirmModal";
import ProfilePostsFeed from "../../components/ProfilePosts/ProfilePostsFeed";

// ✅ Import New Components
import ProfileTabs from "../../components/Profile/ProfileTabs";
import ProfileAbout from "../../components/Profile/ProfileAbout";
import {
    FriendActions,
    MutualFriendsList,
    SocialLinks
} from "../../components/PublicProfile";

import "./PublicProfile.css"; // Keep for layout specific to this page

const PublicProfile = () => {
    const { username } = useParams();
    const { user } = useContext(AuthContext);
    const { openChatWithFriend } = useChatContext();

    const {
        profile,
        notFound,
        isOwnProfile,
        mutualFriends,
        page,
        totalPages,
        alreadyFriends,
        requestSent,
        isBlocked,
        incomingRequest,
        setPage,
        setRequestSent,
        setAlreadyFriends,
        setIncomingRequest,
    } = usePublicProfileData(username);

    const [modalOpen, setModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState("");
    const [activeTab, setActiveTab] = useState('posts');

    // --- UTILITIES ---
    const copyProfileLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.info("Profile link copied to clipboard!");
    };

    const handleMessageUser = async () => {
        if (!profile?._id) return;
        try {
            await openChatWithFriend(profile._id);
            toast.info(`Opening chat...`);
        } catch (err) {
            toast.error("Could not open chat at this time.");
        }
    };

    // --- FRIEND LOGIC ---
    const sendFriendRequest = async () => {
        if (!profile) return;
        try {
            const res = await authPost(`/friends/send-request/${profile._id}`);
            if (res.ok) {
                setRequestSent(true);
                toast.success(`Request sent to ${profile.nickname || profile.username}`);
            } else {
                toast.error("Failed to send request.");
            }
        } catch {
            toast.error("Network error.");
        }
    };

    const cancelRequest = async () => {
        try {
            const res = await authFetch(`/friends/cancel-request/${profile._id}`, { method: "DELETE" });
            if (res.ok) {
                setRequestSent(false);
                toast.info("Request cancelled.");
            }
        } catch { toast.error("Error cancelling request."); }
    };

    const unfriend = async () => {
        try {
            const res = await authFetch(`/friends/remove/${profile._id}`, { method: "DELETE" });
            if (res.ok) {
                setAlreadyFriends(false);
                toast.success("User removed.");
            }
        } catch { toast.error("Error unfriending."); }
    };

    const acceptRequest = async () => {
        try {
            const res = await authFetch(`/friends/respond/${profile._id}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ accepted: true }),
            });
            if (res.ok) {
                setIncomingRequest(false);
                setAlreadyFriends(true);
                toast.success("Request accepted!");
            }
        } catch { toast.error("Error accepting request."); }
    };

    const handleReportUser = async () => {
        if (!user || isOwnProfile) return;
        const reason = window.prompt(`Report ${profile.username}? Reason:`);
        if (!reason) return;

        try {
            const res = await authFetch("/reports", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ targetUserId: profile._id, type: "user", reason }),
            });
            if (res.ok) toast.warning("Report submitted.");
        } catch { toast.error("Error reporting."); }
    };

    const handleConfirm = () => {
        if (pendingAction === "accept") acceptRequest();
        if (pendingAction === "unfriend") unfriend();
        setModalOpen(false);
    };

    if (notFound) return <div className="page-wrapper"><p>User not found.</p></div>;
    if (!profile) return <div className="page-wrapper"><div className="spinner"></div></div>;

    const coverImage = profile.coverPicture || "https://via.placeholder.com/1200x300/3b82f6/ffffff?text=Cover+Image";

    return (
        <div className="page-wrapper">
            <div className="profile-header">
                <div className="cover-image" style={{ backgroundImage: `url(${coverImage})` }} />
            </div>

            <div className="profile-content">
                <div className="profile-identity">
                    <div className="avatar-wrapper">
                        <img src={profile.profilePicture || "/default-avatar.png"} alt={profile.username} className="profile-avatar" />
                        {profile.isOnline && <div className="status-indicator online"></div>}
                    </div>

                    <h1 className="profile-name">{profile.nickname || profile.username}</h1>
                    <p className="profile-nickname">@{profile.username}</p>
                    {profile.bio && <p className="profile-bio">{profile.bio}</p>}

                    <SocialLinks socialLinks={profile.socialLinks} />

                    <div className="profile-stats">
                        {profile.location && <><span>{profile.location}</span><span>•</span></>}
                        <span><strong>{mutualFriends.length || 0}</strong> Mutuals</span>
                    </div>

                    <div className="profile-actions-top">
                        {user && !isOwnProfile && (
                            <button className="btn-message" onClick={handleMessageUser}>✉️ Message</button>
                        )}
                        <button className="btn-share" onClick={copyProfileLink}>🔗 Share</button>
                    </div>

                    {user && (
                        <FriendActions
                            isCurrentUser={isOwnProfile}
                            alreadyFriends={alreadyFriends}
                            requestSent={requestSent}
                            incomingRequest={incomingRequest}
                            isBlocked={isBlocked}
                            onSend={sendFriendRequest}
                            onCancel={cancelRequest}
                            onAccept={acceptRequest}
                            onModalOpen={(action) => { setPendingAction(action); setModalOpen(true); }}
                        />
                    )}

                    {user && !isOwnProfile && (
                        <div style={{ marginTop: '15px' }}>
                            <button className="report-user-button" onClick={handleReportUser}>🚨 Report User</button>
                        </div>
                    )}
                </div>

                {/* ✅ REFACTORED TABS */}
                <ProfileTabs activeTab={activeTab} setActiveTab={setActiveTab} />

                <div className="tab-content">
                    {activeTab === 'posts' && (
                        <div className="fade-in">
                            <ProfilePostsFeed profileUserId={profile._id} isOwnProfile={isOwnProfile} />
                        </div>
                    )}

                    {/* ✅ REFACTORED ABOUT SECTION */}
                    {activeTab === 'about' && <ProfileAbout profile={profile} />}

                    {activeTab === 'friends' && (
                        <div className="fade-in">
                            <MutualFriendsList
                                mutualFriends={mutualFriends}
                                page={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                            />
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={modalOpen}
                title={pendingAction === "accept" ? "Accept Request" : "Unfriend User"}
                message={`Are you sure you want to ${pendingAction} this user?`}
                onCancel={() => setModalOpen(false)}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default PublicProfile;